import { BaseService, notifyInactiveBroker } from '../../base';
import type {
  AttachPaymentInfoResponse,
  AttachQuotePayload,
  CalculateProposalResponse,
  CapitalGlobalProposalAttachmentsResponse,
  CapitalGlobalProposalAttachment,
  ChooseProposalSignatureMethodPayload,
  DocusignSignatureResponse,
  GeneratePdfPayload,
  GeneratePdfResponse,
  GetProposalDocumentResponse,
  PaymentInfoPayload,
  Proposal,
  ProposalSignatureMethodResponse,
  RegisterCapitalGlobalProposalAttachmentPayload,
} from '../types';

const PORTAL_API_URL = import.meta.env.VITE_API_URL as string | undefined;
const PRODUCT_PREFIX = '/portal/v1/global/proposals';

/**
 * Serviço específico para proposta Capital Global. Mantém o fluxo de proposta
 * no controller global, sem cair no fallback residencial.
 */
export class CapitalGlobalProposalsService extends BaseService {
  constructor() {
    super();
  }

  async attachPaymentInfo(
    policyId: string,
    payload: PaymentInfoPayload
  ): Promise<AttachPaymentInfoResponse> {
    try {
      const sanitizedPolicyId = encodeURIComponent(policyId.trim());
      const path = `${PRODUCT_PREFIX}/${sanitizedPolicyId}/attach-element`;
      const response = await this.post(path, payload, PORTAL_API_URL);

      if (response.status === 201) {
        const json = await this.handleJsonResponse<unknown>(response);
        return {
          success: true,
          message: json.message,
        };
      }

      const json = await this.handleJsonResponse<unknown>(response);
      return {
        success: false,
        error: json.error || 'Erro ao anexar informações de pagamento',
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao anexar informações de pagamento',
      };
    }
  }

  async updateProposal(
    proposalId: string,
    payload: Partial<AttachQuotePayload>
  ): Promise<{
    success: boolean;
    data?: Proposal;
    error?: string;
  }> {
    try {
      const path = `${PRODUCT_PREFIX}/${encodeURIComponent(proposalId)}/attach-element`;
      const response = await this.post(path, payload, PORTAL_API_URL);
      return this.handleJsonResponse<Proposal>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao atualizar proposta',
      };
    }
  }

  async calculateProposal(
    policyId: string
  ): Promise<CalculateProposalResponse> {
    try {
      const sanitizedPolicyId = encodeURIComponent(policyId.trim());
      const path = `${PRODUCT_PREFIX}/${sanitizedPolicyId}/calculate`;
      const response = await this.post(path, {}, PORTAL_API_URL);

      if (!response.ok) {
        const json = await this.handleJsonResponse<unknown>(response);
        return {
          success: false,
          error:
            json.error ||
            `Erro ao calcular proposta (${response.status}: ${response.statusText})`,
        };
      }

      const json = await this.handleJsonResponse<unknown>(response);

      if (!json.success) {
        return {
          success: false,
          error: json.error || 'Erro ao calcular proposta',
        };
      }

      return {
        success: true,
        data: json.data,
        message: json.message,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro inesperado ao calcular proposta',
      };
    }
  }

  async generatePdf(
    proposalId: string,
    payload: GeneratePdfPayload
  ): Promise<{
    success: boolean;
    data?: GeneratePdfResponse;
    error?: string;
  }> {
    try {
      const path = `${PRODUCT_PREFIX}/${encodeURIComponent(proposalId)}/pdf`;
      const response = await this.post(path, payload, {}, PORTAL_API_URL);

      if (!response.ok) {
        return await extractApiError(response);
      }

      const data = await this.handleJsonResponse<GeneratePdfResponse>(response);

      if (!data.success || !data.data) {
        return {
          success: false,
          error: data.error || 'Erro ao gerar URL do PDF',
        };
      }

      return { success: true, data: data.data };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao gerar PDF da proposta',
      };
    }
  }

  async getProposalDocument(proposalId: string): Promise<{
    success: boolean;
    data?: GetProposalDocumentResponse;
    error?: string;
  }> {
    try {
      const path = `${PRODUCT_PREFIX}/${encodeURIComponent(proposalId)}/documents`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return this.handleJsonResponse<GetProposalDocumentResponse>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao carregar documento da proposta',
      };
    }
  }

  async listAttachments(
    policyId: string,
    documentType?: string
  ): Promise<{
    success: boolean;
    data?: CapitalGlobalProposalAttachmentsResponse;
    error?: string;
  }> {
    try {
      const sanitizedPolicyId = encodeURIComponent(policyId.trim());
      const query = documentType
        ? `?documentType=${encodeURIComponent(documentType)}`
        : '';
      const path = `${PRODUCT_PREFIX}/${sanitizedPolicyId}/attachments${query}`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      // A lista carrega ao abrir a tela (apólice/proposta): com corretora
      // inativa a Api responde 403, mas os botões já estão travados e a tela
      // já tem o aviso — sem toast aqui.
      return this.handleJsonResponse<CapitalGlobalProposalAttachmentsResponse>(
        response,
        { notifyInactiveBroker: false }
      );
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao listar anexos da proposta',
      };
    }
  }

  async registerAttachment(
    policyId: string,
    payload: RegisterCapitalGlobalProposalAttachmentPayload
  ): Promise<{
    success: boolean;
    data?: CapitalGlobalProposalAttachment;
    error?: string;
  }> {
    try {
      const sanitizedPolicyId = encodeURIComponent(policyId.trim());
      const path = `${PRODUCT_PREFIX}/${sanitizedPolicyId}/attachments`;
      const response = await this.post(path, payload, PORTAL_API_URL);
      return this.handleJsonResponse<CapitalGlobalProposalAttachment>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao registrar anexo da proposta',
      };
    }
  }

  async softDeleteAttachment(
    policyId: string,
    attachmentId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const sanitizedPolicyId = encodeURIComponent(policyId.trim());
      const sanitizedAttachmentId = encodeURIComponent(attachmentId.trim());
      const path = `${PRODUCT_PREFIX}/${sanitizedPolicyId}/attachments/${sanitizedAttachmentId}`;
      const response = await this.delete(path, {}, PORTAL_API_URL);

      if (response.status === 204 || response.ok) {
        return { success: true };
      }

      const json = await this.handleJsonResponse<unknown>(response);
      return {
        success: false,
        error: json.error || 'Erro ao excluir anexo da proposta',
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao excluir anexo da proposta',
      };
    }
  }

  async transmitProposal(policyId: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const sanitizedPolicyId = encodeURIComponent(policyId.trim());
      const path = `${PRODUCT_PREFIX}/${sanitizedPolicyId}/transmit`;
      const response = await this.post(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<void>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao transmitir proposta',
      };
    }
  }

  async createDocusignSignature(policyId: string): Promise<{
    success: boolean;
    data?: DocusignSignatureResponse;
    error?: string;
    statusCode?: number;
  }> {
    return this.postDocusignSignatureAction(policyId, 'signatures/docusign');
  }

  async getProposalSignatureMethod(policyId: string): Promise<{
    success: boolean;
    data?: ProposalSignatureMethodResponse;
    error?: string;
    notFound?: boolean;
  }> {
    try {
      const sanitizedPolicyId = encodeURIComponent(policyId.trim());
      const path = `${PRODUCT_PREFIX}/${sanitizedPolicyId}/signatures/method`;
      const response = await this.get(path, {}, PORTAL_API_URL);

      if (response.status === 404) {
        return { success: true, data: { method: null, locked: false }, notFound: true };
      }

      if (!response.ok) {
        return await extractApiError(response);
      }

      const parsed =
        await this.handleJsonResponse<ProposalSignatureMethodResponse>(response);

      if (!parsed.success || !parsed.data) {
        return {
          success: false,
          error: parsed.error || 'Erro ao consultar método de assinatura',
        };
      }

      return { success: true, data: parsed.data };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao consultar método de assinatura',
      };
    }
  }

  async chooseProposalSignatureMethod(
    policyId: string,
    payload: ChooseProposalSignatureMethodPayload
  ): Promise<{
    success: boolean;
    data?: ProposalSignatureMethodResponse;
    error?: string;
    statusCode?: number;
  }> {
    try {
      const sanitizedPolicyId = encodeURIComponent(policyId.trim());
      const path = `${PRODUCT_PREFIX}/${sanitizedPolicyId}/signatures/method`;
      const response = await this.post(path, payload, PORTAL_API_URL);

      if (!response.ok) {
        const failure = await extractApiError(response);
        return { ...failure, statusCode: response.status };
      }

      const parsed =
        await this.handleJsonResponse<ProposalSignatureMethodResponse>(response);

      if (!parsed.success || !parsed.data) {
        return {
          success: false,
          error: parsed.error || 'Erro ao registrar método de assinatura',
          statusCode: response.status,
        };
      }

      return { success: true, data: parsed.data, statusCode: response.status };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao registrar método de assinatura',
      };
    }
  }

  async getDocusignSignatureStatus(policyId: string): Promise<{
    success: boolean;
    data?: DocusignSignatureResponse | null;
    error?: string;
    notFound?: boolean;
  }> {
    try {
      const sanitizedPolicyId = encodeURIComponent(policyId.trim());
      const path = `${PRODUCT_PREFIX}/${sanitizedPolicyId}/signatures/docusign`;
      const response = await this.get(path, {}, PORTAL_API_URL);

      if (response.status === 404) {
        return { success: true, data: null, notFound: true };
      }

      if (!response.ok) {
        return await extractApiError(response);
      }

      const parsed =
        await this.handleJsonResponse<DocusignSignatureResponse>(response);

      if (!parsed.success || !parsed.data) {
        return {
          success: false,
          error: parsed.error || 'Erro ao consultar assinatura digital',
        };
      }

      return { success: true, data: parsed.data };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao consultar assinatura digital',
      };
    }
  }

  async syncDocusignSignature(policyId: string): Promise<{
    success: boolean;
    data?: DocusignSignatureResponse;
    error?: string;
    statusCode?: number;
  }> {
    return this.postDocusignSignatureAction(
      policyId,
      'signatures/docusign/sync'
    );
  }

  async retryDocusignSignature(policyId: string): Promise<{
    success: boolean;
    data?: DocusignSignatureResponse;
    error?: string;
    statusCode?: number;
  }> {
    return this.postDocusignSignatureAction(
      policyId,
      'signatures/docusign/retry'
    );
  }

  private async postDocusignSignatureAction(
    policyId: string,
    actionPath: string
  ): Promise<{
    success: boolean;
    data?: DocusignSignatureResponse;
    error?: string;
    statusCode?: number;
  }> {
    try {
      const sanitizedPolicyId = encodeURIComponent(policyId.trim());
      const path = `${PRODUCT_PREFIX}/${sanitizedPolicyId}/${actionPath}`;
      const response = await this.post(path, {}, PORTAL_API_URL);

      if (!response.ok) {
        const failure = await extractApiError(response);
        return { ...failure, statusCode: response.status };
      }

      const parsed =
        await this.handleJsonResponse<DocusignSignatureResponse>(response);

      if (!parsed.success || !parsed.data) {
        return {
          success: false,
          error: parsed.error || 'Erro na assinatura digital',
          statusCode: response.status,
        };
      }

      return { success: true, data: parsed.data, statusCode: response.status };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro na assinatura digital',
      };
    }
  }
}

async function extractApiError(
  response: Response
): Promise<{ success: false; error: string }> {
  const text = (await response.text()).trim();
  let apiMessage: string | undefined;

  if (text) {
    try {
      const data = JSON.parse(text) as Record<string, unknown>;
      const problemDetail = typeof data.detail === 'string' && data.detail;
      const direct =
        (typeof data.message === 'string' && data.message) ||
        (typeof data.error === 'string' && data.error);
      const problemTitle = typeof data.title === 'string' && data.title;
      const errors = data.errors;

      if (problemDetail) apiMessage = problemDetail;
      else if (direct) apiMessage = direct;
      else if (problemTitle) apiMessage = problemTitle;
      else if (Array.isArray(errors) && errors.length > 0) {
        const first = errors[0] as unknown;
        if (typeof first === 'string') apiMessage = first;
        else if (first && typeof first === 'object') {
          const e = first as Record<string, unknown>;
          const msg = typeof e.message === 'string' && e.message;
          if (msg) apiMessage = msg;
        }
      }
    } catch {
      apiMessage = text;
    }
  }

  // Este caminho não passa pelo `handleJsonResponse` (ex.: PDF): avisa aqui.
  notifyInactiveBroker(response.status, apiMessage);

  return {
    success: false,
    error: apiMessage || `Erro ${response.status}: ${response.statusText}`,
  };
}

export const capitalGlobalProposalsService =
  new CapitalGlobalProposalsService();
